import { useMemo, useState } from "react"
import { Box, Chip, IconButton, TableCell, TableRow, Tooltip, Typography } from "@mui/material"
import { If } from "lib/@components/switch"
import { Repeat } from "lib/@components/repeat"
import Iconify from "minimals-template/components/Iconify"
import { formatWorkingDays } from "../format-utils"
import { CostBreakdownDetailRow } from "./cost-breakdown-detail-row"
import { ALLOCATED_FIXED_CONTRACT_TOOLTIP, buildCostBreakdownDetailRows } from "./cost-breakdown-view-utils"
import { CostFigure } from "./cost-figure"
import { useBoundContext } from "lib/@components/binding/use-bound-context"

export function CostBreakdownRow({ item, currencySymbol, showBillingAdjustment, showTaskCount }) {
    const { hoursPerDay } = useBoundContext()
    const [open, setOpen] = useState(false)
    const hasExpandable = getHasExpandable()
    const detailRows = useMemo(getDetailRows, [item, open, hasExpandable])
    const actualHours = item.actualHours ?? item.totalHours
    const isFixed = item.mode === "fixed-annual"
    const hasAllocatedFixedCost = item.allocatedFixedCost > 0
    const expandIcon = getExpandIcon()

    return (
        <>
            <TableRow data-cy="cost-resource-row">
                <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                        <If value={hasExpandable}>
                            <IconButton
                                aria-label={open ? "Collapse row" : "Expand row"}
                                size="small"
                                onClick={handleToggle}
                                data-cy="expand-cost-breakdown-row"
                            >
                                <Iconify icon={expandIcon} width={20} />
                            </IconButton>
                        </If>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2">{item.label}</Typography>
                            <If value={!!item.secondaryLabel}>
                                <Typography variant="caption" color="text.secondary">
                                    {item.secondaryLabel}
                                </Typography>
                            </If>
                        </Box>
                        <If value={hasAllocatedFixedCost}>
                            <Tooltip title={ALLOCATED_FIXED_CONTRACT_TOOLTIP} arrow>
                                <Chip
                                    label="Allocated fixed contract"
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                    data-cy="allocated-fixed-contract-chip"
                                />
                            </Tooltip>
                        </If>
                        <If value={isFixed && !hasAllocatedFixedCost}>
                            <Chip
                                label="Fixed contract"
                                size="small"
                                color="info"
                                variant="outlined"
                                data-cy="fixed-contract-chip"
                            />
                        </If>
                    </Box>
                </TableCell>
                <If value={showTaskCount}>
                    <TableCell align="right">
                        <Typography variant="body2">{item.taskCount || 0}</Typography>
                    </TableCell>
                </If>
                <If value={showBillingAdjustment}>
                    <TableCell align="right">
                        <Typography variant="body2">{formatWorkingDays(actualHours, hoursPerDay)}</Typography>
                    </TableCell>
                </If>
                <TableCell align="right">
                    <Typography variant="body2">{formatWorkingDays(item.totalHours, hoursPerDay)}</Typography>
                </TableCell>
                <TableCell align="right">
                    <If value={isFixed && !hasAllocatedFixedCost}>
                        <Typography variant="body2" color="text.secondary">
                            {currencySymbol}0 (fixed contract)
                        </Typography>
                    </If>
                    <If value={!isFixed || hasAllocatedFixedCost}>
                        <CostFigure value={item.totalCost} currencySymbol={currencySymbol} />
                    </If>
                </TableCell>
            </TableRow>
            <If value={open && hasExpandable}>
                <Repeat
                    list={detailRows}
                    keyFn="id"
                    item={
                        <CostBreakdownDetailRow
                            currencySymbol={currencySymbol}
                            showBillingAdjustment={showBillingAdjustment}
                            showTaskCount={showTaskCount}
                        />
                    }
                />
            </If>
        </>
    )

    function handleToggle() {
        setOpen(!open)
    }

    function getHasExpandable() {
        if ("hasDetailRows" in item) return item.hasDetailRows
        return (item.detailRows || []).length > 0
    }

    function getDetailRows() {
        if (!open || !hasExpandable) return []
        return buildCostBreakdownDetailRows(item)
    }

    function getExpandIcon() {
        if (open) return "mdi:chevron-down"
        return "mdi:chevron-right"
    }
}
